import { AttributeValue } from "./AttributeValue";
import { AttributeView } from "./AttributeView";
import { ExportValue } from "./ExportValue";
import { Feature } from "ol";
import { FeatureType } from "@/geoMap/define/FeatureType";
import { IAttributeConvert } from "./IAttributeConvert";

/**
 * 画面幅
 */
const VALUE_WIDTH = 470;

/**
 * 画面高さ
 */
const VALUE_HEIGHT = 343;

/**
 * 出力JSONファイル名
 */
const VALUE_JSON_FILE_NAME = "acc_stat_yyyyMMdd_HHmmss.geojson";

/**
 * 出力CSVファイル名
 */
const VALUE_CSV_FILE_NAME = "acc_stat_yyyyMMdd_HHmmss.csv";

/**
 * 交通事故統計情報属性変換用クラス
 */
export class AccidentHotspotAttributeConvert implements IAttributeConvert {
  /**
   * 地物
   */
  feature: Feature;

  /**
   * 属性画面タイトル
   */
  title: string;

  constructor(feature: Feature) {
    this.feature = feature;
    this.title = `交通事故統計情報`;
  }

  /**
   * 地物タイプ取得
   */
  getFeatureType(): FeatureType {
    return FeatureType.AccidentHotspot;
  }

  /**
   * 画面用フォーマット取得
   */
  getViewFormatData(): AttributeView {
    const properties = this.feature.getProperties();

    // 画面項目定義リスト
    const items = [
      {
        key: "発生日時",
        value:
          properties.発生日時_年 &&
          properties.発生日時_月 &&
          properties.発生日時_日 &&
          properties.発生日時_時 &&
          properties.発生日時_分
            ? `${properties.発生日時_年}/${properties.発生日時_月}/${properties.発生日時_日} ${properties.発生日時_時}:${properties.発生日時_分}`
            : "-",
        isSimple: true,
      },
      {
        key: "事故内容",
        value: properties.事故内容_名称,
        isSimple: true,
      },
      {
        key: "事故類型",
        value: properties.事故類型_名称,
        isSimple: true,
      },
      {
        key: "年齢（当事者A）",
        value: properties["年齢（当事者A）_名称"],
        isSimple: true,
      },
      {
        key: "年齢（当事者B）",
        value: properties["年齢（当事者B）_名称"],
        isSimple: true,
      },
      {
        key: "当事者種別（当事者A）",
        value: properties["当事者種別（当事者A）_名称"],
        isSimple: true,
      },
      {
        key: "当事者種別（当事者B）",
        value: properties["当事者種別（当事者B）_名称"],
        isSimple: true,
      },
      {
        key: "昼夜",
        value: properties.昼夜_名称,
        isSimple: true,
      },
      {
        key: "天候",
        value: properties.天候_名称,
        isSimple: true,
      },
      {
        key: "人身損傷程度（当事者A）",
        value: properties["人身損傷程度（当事者A）_名称"],
        isSimple: false,
      },
      {
        key: "人身損傷程度（当事者B）",
        value: properties["人身損傷程度（当事者B）_名称"],
        isSimple: false,
      },
      {
        key: "中央分離帯施設等",
        value: properties.中央分離帯施設等_名称,
        isSimple: false,
      },
      {
        key: "歩車道区分",
        value: properties.歩車道区分_名称,
        isSimple: false,
      },
      {
        key: "速度規制（指定のみ）（当事者A）",
        value: properties["速度規制（指定のみ）（当事者A）_名称"],
        isSimple: false,
      },
      {
        key: "速度規制（指定のみ）（当事者B）",
        value: properties["速度規制（指定のみ）（当事者B）_名称"],
        isSimple: false,
      },
      {
        key: "死者数",
        value: properties.死者数,
        isSimple: false,
      },
      {
        key: "負傷者数",
        value: properties.負傷者数,
        isSimple: false,
      },
      {
        key: "地形",
        value: properties.地形_名称,
        isSimple: false,
      },
      {
        key: "路面状態",
        value: properties.路面状態_名称,
        isSimple: false,
      },
      {
        key: "道路形状",
        value: properties.道路形状_名称,
        isSimple: false,
      },
      {
        key: "信号機",
        value: properties.信号機_名称,
        isSimple: false,
      },
      {
        key: "車道幅員",
        value: properties.車道幅員_名称,
        isSimple: false,
      },
      {
        key: "衝突地点",
        value: properties.衝突地点_名称,
        isSimple: false,
      },
      {
        key: "用途別（当事者A）",
        value: properties["用途別（当事者A）_名称"],
        isSimple: false,
      },
      {
        key: "用途別（当事者B）",
        value: properties["用途別（当事者B）_名称"],
        isSimple: false,
      },
      {
        key: "車両形状等（当事者A）",
        value: properties["車両形状等（当事者A）_名称"],
        isSimple: false,
      },
      {
        key: "車両形状等（当事者B）",
        value: properties["車両形状等（当事者B）_名称"],
        isSimple: false,
      },
      {
        key: "都道府県コード",
        value:
          properties.都道府県コード && properties.都道府県コード_名称
            ? `${properties.都道府県コード}（${properties.都道府県コード_名称}）`
            : "-",
        isSimple: false,
      },
      {
        key: "警察署等コード",
        value:
          properties.警察署等コード && properties.警察署等コード_名称
            ? `${properties.警察署等コード}（${properties.警察署等コード_名称}）`
            : "-",
        isSimple: false,
      },
      {
        key: "本票番号",
        value: properties.本票番号,
        isSimple: false,
      },
      {
        key: "路線コード",
        value:
          properties.路線コード && properties.路線コード_名称
            ? `${properties.路線コード}（${properties.路線コード_名称}）`
            : "-",
        isSimple: false,
      },
      {
        key: "地点コード",
        value: properties.地点コード,
        isSimple: false,
      },
      {
        key: "市区町村コード",
        value:
          properties.市区町村コード && properties.市区町村コード_名称
            ? `${properties.市区町村コード}（${properties.市区町村コード_名称}）`
            : "-",
        isSimple: false,
      },
    ];
    // 画面項目リストでAttributeValue配列を生成しクラス変数に格納後、return
    const attributeValues: AttributeValue[] = [];
    items.forEach((item) => {
      attributeValues.push(
        new AttributeValue(
          item.key,
          item.value ? item.value.toString() : null,
          item.isSimple
        )
      );
    });
    const attributeView = new AttributeView(
      attributeValues,
      this.title,
      VALUE_WIDTH,
      VALUE_HEIGHT,
      VALUE_HEIGHT,
      null,
      false,
      false
    );
    return attributeView;
  }

  /**
   * ファイル出力用項目取得
   */
  getExportValue(): ExportValue {
    const feature = this.feature.clone();
    feature.getGeometry().transform("EPSG:3857", "EPSG:4326");
    const attributeValues = this.getViewFormatData().attributeValues;
    return new ExportValue(
      feature,
      attributeValues,
      VALUE_JSON_FILE_NAME,
      VALUE_CSV_FILE_NAME
    );
  }
}
